'use client';

import dynamic from 'next/dynamic';
import { CalendarDays, PhoneCall } from 'lucide-react';
import { GlassCard, V4GhostButton } from '@/components/landing/v4-shared';
import { ELEVENLABS_LIVE_AGENT_IDS } from '@config/elevenlabs';

const FloatingVoiceV1 = dynamic(() => import('@/components/voice/FloatingVoiceV1'), {
  ssr: false,
});

// Dedicated Enterprise Sales agent (agent_0401) — enterprise discovery flow +
// capture_enterprise_lead webhook. This public enterprise page should always
// exercise the Enterprise prompt, including on localhost QA.
const ENTERPRISE_AGENT_ID =
  ELEVENLABS_LIVE_AGENT_IDS.enterprise;

export function EnterpriseBriVoice() {
  return (
    <div className="mx-auto grid max-w-5xl items-center gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(320px,1fr)]">
      <div>
        <p
          className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1"
          style={{
            border: '1px solid rgba(125,250,255,0.2)',
            background: 'rgba(34,211,238,0.1)',
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 11,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--cyan-glow)',
          }}
        >
          <PhoneCall className="h-3.5 w-3.5" aria-hidden />
          Enterprise options
        </p>
        <h2
          id="enterprise-bri-heading"
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontWeight: 600,
            fontSize: 'clamp(28px, 4vw, 48px)',
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            color: 'var(--paper)',
            margin: 0,
          }}
        >
          Tap to Talk to Bri about Enterprise.
        </h2>
        <p
          style={{
            marginTop: 16,
            maxWidth: '36rem',
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--dim)',
          }}
        >
          Already running on BizRnR? Tell Bri how many calls you handle, which channels matter, and
          where your current workflow breaks. She captures requirements and routes the right upgrade.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <V4GhostButton href="https://cal.com/bizrnr" variant="cyan">
            <CalendarDays size={14} aria-hidden />
            Schedule instead
          </V4GhostButton>
          <V4GhostButton href="/pricing" variant="paper">
            Back to pricing
          </V4GhostButton>
        </div>
      </div>

      <GlassCard accent="cyan" hoverable padding={32}>
        <p
          style={{
            marginBottom: 16,
            textAlign: 'center',
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 11,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.22em',
            color: 'var(--cyan-glow)',
          }}
        >
          Live AI voice
        </p>
        <div className="flex justify-center" data-testid="enterprise-pricing-voice-widget">
          <FloatingVoiceV1
            surface="enterprise_pricing"
            variant="centered-ring"
            agentId={ENTERPRISE_AGENT_ID}
            helperText={{
              idle: 'Tap the ring and ask Bri about Enterprise pricing, integrations, and rollout.',
              connecting: 'Connecting Bri to the Enterprise voice prompt.',
              listening: 'Bri is listening. Tell her what Enterprise should automate for you.',
              speaking: 'Bri is talking. She may ask about call volume, integrations, compliance, and rollout.',
              ended: 'Call ended. Tap to talk again.',
            }}
            postCall={{ mode: 'restart' }}
          />
        </div>
      </GlassCard>
    </div>
  );
}
